"use client";

import { useState, type ReactNode } from "react";
import { Check } from "lucide-react";

import BillingToggle from "@/components/plans/BillingToggle";
import PricingCard from "@/components/plans/PricingCard";
import { formatPlanPrice } from "@/lib/plan-api";
import {
  foodCommercialPresentation as presentation,
} from "@/lib/food-commercial-plans";
import type { CommercialOffer } from "@/types/commercial-offer";

export default function FoodPricingSection({ modalitySelector, offers, errorMessage = "", embedded = false, showComparison = false }: { modalitySelector?: ReactNode; offers: CommercialOffer[]; errorMessage?: string; embedded?: boolean; showComparison?: boolean }) {
  const Heading = embedded ? "h2" : "h1";
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");
  const annualDefined = offers.length > 0 && offers.every(offer => !offer.checkout_available && offer.yearly_price !== null);
  const cycle = annualDefined ? billingCycle : "monthly";
  const available = offers.some(offer => offer.checkout_available);
  const financial = offers.some(offer => offer.checkout_available && offer.contract?.billing_mode === "asaas");
  const sandbox = financial && offers.some(offer => offer.contract?.billing_environment === "sandbox");
  return (
    <section className="bg-slate-50 px-5 py-16 lg:px-8 lg:py-24">
      <div className="mx-auto max-w-[1280px]">
        <header className="text-center">
          <p className="text-sm font-bold uppercase tracking-[0.16em] text-blue-600">{presentation.title}</p>
          {modalitySelector}
          <Heading className="mx-auto text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">{presentation.headline}</Heading>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600">{presentation.description}</p>
          <div className="mx-auto mt-6 max-w-2xl rounded-xl border border-blue-200 bg-blue-50 px-5 py-4 text-sm leading-6 text-blue-950">
            <p className="font-bold">{financial ? sandbox ? "Contratação em testes — Asaas sandbox" : "Contratação pelo Asaas" : available ? "Contratação simulada disponível em DEV" : presentation.status}</p>
            <p>{financial ? "Escolha seu plano e a forma de pagamento. Acesso após confirmação financeira, com renovação mensal. Direitos efetivos são apresentados na contratação." : available ? "Sem transações financeiras reais. Ativação após confirmação do pagamento simulado por administrador. Direitos efetivos são apresentados na contratação." : presentation.notice}</p>
          </div>
          <div className="mt-7">
            <BillingToggle value={cycle} onChange={setBillingCycle} yearlyDisabled={!annualDefined} yearlyDescriptionId="food-period-notice" />
          </div>
          <p id="food-period-notice" className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-600">{annualDefined ? "Referências comerciais propostas. Contratação em preparação." : presentation.periodNotice}</p>
        </header>

        <ul aria-label="Condições da proposta Food" className="mx-auto mt-8 flex max-w-4xl flex-wrap justify-center gap-x-7 gap-y-3 text-sm font-semibold text-slate-700">
          {presentation.terms.map(term => (
            <li key={term} className="flex items-center gap-2"><Check size={17} className="shrink-0 text-blue-600" aria-hidden="true" />{term}</li>
          ))}
        </ul>
        <p className="mx-auto mt-4 max-w-3xl text-center text-xs leading-6 text-slate-600">
          Condições do canal próprio. Mensalidades, taxas dos meios de pagamento e serviços externos podem se aplicar.
        </p>

        {errorMessage ? <div role="alert" className="mx-auto mt-10 max-w-xl rounded-xl border border-red-200 bg-white p-6 text-center text-sm text-red-800">{errorMessage} <button type="button" onClick={() => window.location.reload()} className="font-bold underline">Tentar novamente</button></div> : offers.length === 0 ? <p className="mt-10 text-center text-slate-600">Nenhuma oferta Food publicada no momento.</p> : null}
        <div className="mx-auto mt-14 grid max-w-[1280px] items-stretch gap-7 md:grid-cols-2 xl:grid-cols-3">
          {offers.map(offer => {
            const price = cycle === "yearly" ? offer.yearly_price : offer.monthly_price;
            const limits = [
              (offer.checkout_available ? offer.contract?.user_limit : offer.user_limit) == null ? "Usuários em definição" : `Até ${offer.checkout_available ? offer.contract?.user_limit : offer.user_limit} usuários (${offer.checkout_available ? "contrato" : "proposta"})`,
              ...(offer.product_limit === null ? [] : [`Até ${offer.product_limit} itens no cardápio (proposta)`]),
              ...(offer.category_limit === null ? [] : [`Até ${offer.category_limit} categorias (proposta)`]),
              ...(offer.image_limit_per_product === null ? [] : [`Até ${offer.image_limit_per_product} imagens por item (proposta)`]),
              ...(offer.whatsapp_limit === null ? [] : [`Até ${offer.whatsapp_limit} WhatsApps (proposta)`]),
              ...(offer.support_sla_hours === null ? [] : [`SLA de suporte proposto: ${offer.support_sla_hours} horas`]),
            ];
            return (
            <PricingCard
              key={offer.id}
              compact={embedded}
              name={offer.name}
              description={offer.description}
              price={price === null ? "Em definição" : formatPlanPrice(price)}
              period={price === null ? undefined : cycle === "yearly" ? "/ano" : "/mês"}
              priceNotice={offer.checkout_available ? offer.contract?.billing_mode === "asaas" ? "Assinatura mensal pelo Asaas" : "Valor mensal da simulação DEV" : cycle === "yearly" ? presentation.annualPriceNotice : presentation.priceNotice}
              features={[...limits, ...offer.benefits.map(benefit => benefit.name)]}
              featureNotice={offer.feature_notice}
              buttonLabel={offer.checkout_available ? offer.contract?.billing_mode === "asaas" ? "Contratar plano Food" : "Simular contratação em DEV" : presentation.actionLabel}
              href={offer.checkout_available ? `/cadastro?plan=${offer.code}&billing=monthly` : undefined}
              disabled={!offer.checkout_available}
              showPaymentNotice={false}
              highlighted={offer.highlighted}
              badge={offer.badge}
            />
          );})}
        </div>
        {showComparison && !errorMessage && offers.length > 0 && (
          <details className="food-comparison">
            <summary>Compare a composição contratual Food</summary>
            <p>Dados do contrato cadastrado. A utilização exige plano vigente, habilitação do estabelecimento e permissões da equipe.
              Limites em definição não representam uma franquia ilimitada.</p>
            <div className="comparison-scroll" role="region" aria-label="Comparação dos contratos Food" tabIndex={0}>
              <table>
                <thead><tr><th scope="col">Recurso</th>{offers.map(offer => <th scope="col" key={offer.id}>{offer.name}</th>)}</tr></thead>
                <tbody>
                  <tr><th scope="row">Usuários no contrato</th>{offers.map(offer => <td key={offer.id}>{offer.contract?.user_limit ?? "Não informado"}</td>)}</tr>
                  {([
                    ["food_online_orders", "Pedidos online"], ["food_delivery", "Delivery"],
                    ["food_pos", "PDV"], ["food_cash", "Caixa"],
                    ["food_management", "Gestão interna"], ["food_kds", "Cozinha / KDS"],
                    ["food_tables", "Mesas e comandas"], ["food_production", "Produção"],
                  ] as const).map(([key, label]) => (
                    <tr key={key}><th scope="row">{label}</th>{offers.map(offer => {
                      const value = offer.contract?.permissions[key];
                      return <td key={offer.id}>{typeof value === "boolean" ? value ? "Sim" : "Não" : "Não informado"}</td>;
                    })}</tr>
                  ))}
                  {([
                    ["product_limit", "Itens no cardápio"], ["category_limit", "Categorias"],
                    ["image_limit_per_product", "Imagens por item"], ["whatsapp_limit", "WhatsApps cadastrados"],
                  ] as const).map(([key, label]) => (
                    <tr key={key}><th scope="row">{label}</th>{offers.map(offer => <td key={offer.id}>{offer.contract?.approved_limits[key] ?? "Em definição"}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </div>
    </section>
  );
}
